import React from 'react';
import PropTypes from 'prop-types';

import Style from './ratesForm.module.css';
import { useTranslation } from 'react-i18next';
import { EmptyState, Icon, Hierarchy, Form, Tooltip } from '@weezevent/nacre';
import LoadingCard from '../../../../components/loadingCard';
import { useHistory } from 'react-router-dom';
import useAppContext from '../../../../context/useAppContext';
import { InputMemo } from '../../actions/quotasAction';

function HierarchyRates({ rates, sells, onChangeForm, form, totalHierarchyErrors = false }) {
    const { t } = useTranslation();

    const blockChange = React.useCallback(event => {
        if (event.key === '.' || event.key === ',') {
            event.preventDefault();
            event.stopPropagation();
        }
    }, []);

    const formRef = React.useRef(form);

    React.useEffect(() => {
        formRef.current = form;
    }, [form]);

    const _onChange = React.useCallback(
        (event, id, value, typeObject = 'rate') => {
            const currentForm = formRef.current;
            if (value && !Number.isInteger(Number(value))) {
                return;
            }

            if (typeObject === 'rate') {
                const newSelected = currentForm.rates;
                newSelected[id] = !value || value === '' ? null : Number(value);
                onChangeForm('rates', { ...newSelected });

                return;
            }

            const newSelected = currentForm.rate_groups;
            newSelected[id] = !value || value === '' ? null : Number(value);

            onChangeForm('rate_groups', { ...newSelected });
        },
        [onChangeForm]
    );

    const onSelected = React.useCallback(
        ({ event, data: { checked } }, node) => {
            event.stopPropagation();
            let newSelected = form.rates;

            if (checked) {
                newSelected[node.value] = null;
            } else {
                delete newSelected[node.value];
            }

            onChangeForm('rates', { ...newSelected });
        },
        [onChangeForm, form.rates]
    );

    const treeRates = React.useMemo(() => {
        const generateComponent = (type, value) => {
            const id = value.id;
            const inputQuotaError =
                type === 'rate' ? form.rates[id] !== null && form.rates[id] < sells?.rates[id] : form.rate_groups[id] !== null && form.rate_groups[id] < sells?.rate_groups[id];
            const inputQuota = (
                <InputMemo
                    key={id}
                    value={(type === 'rate' ? form.rates[id] : form.rate_groups[id]) ?? ''}
                    onChange={_onChange}
                    blockChange={blockChange}
                    id={id}
                    type={type}
                    errors={inputQuotaError}
                />
            );
            const numberSales = type === 'rate' && sells?.rates ? sells.rates[id] : type === 'group' && sells?.rate_groups ? sells.rate_groups[id] : 0;
            return {
                component: (
                    <div className={Style['table-extra-component']}>
                        <span className={Style['sales']}>
                            {numberSales > 0 && (
                                <Tooltip triggerOnHover title={t(`ticket.sessions.detail.sales_${type}`)}>
                                    {numberSales} <Icon name="tickets" />{' '}
                                </Tooltip>
                            )}
                        </span>{' '}
                        {inputQuota}
                    </div>
                ),
                classname: type === 'rate' ? 'row-rate' : value.parent ? 'row-sub-group' : 'row-group'
            };
        };
        return rates.toOptions({
            extraRate: rate => generateComponent('rate', rate),
            extraGroup: group => generateComponent('group', group),
            filterGroup: group => group.removed === null && (group.children.length || group.rates.length)
        });
    }, [sells, rates, form, _onChange, blockChange]);

    return (
        <div className={Style['hierarchy-rates-container']}>
            <Hierarchy
                key={`count_errors_${totalHierarchyErrors}`}
                items={treeRates}
                searchable
                selected={Object.keys(form.rates).map(rid => Number(rid))}
                onSelected={onSelected}
                selectable
                selectableGroup={false}
                withSelectAll={false}
                placeholderSearch={t('ticket.common.action.search')}
                height={'none'}
                chevron
                depth={0}
                widthIndent={'14px'}
            />
        </div>
    );
}

export default function RatesForm({ rates, sells, loadingRates = false, form, onChangeForm = () => {}, errors = [], totalHierarchyErrors = false, idSession = null }) {
    const { t } = useTranslation();

    const [{ event, organization }] = useAppContext();
    const history = useHistory();

    return (
        <div className={Style['rates-form']}>
            <LoadingCard loading={loadingRates || (!sells && idSession)}>
                {!rates ? (
                    <EmptyState
                        icon={<Icon name="list" size="regular" />}
                        title={t('ticket.sessions.detail.rates.empty.title')}
                        labelButton={t('ticket.sessions.detail.rates.empty.labelButton')}
                        onClick={() => {
                            history.push(`/ticket/O${organization.id}/E${event.id}/settings/rates`);
                        }}
                    />
                ) : (
                    <>
                        {errors['rates'] && <Form.FieldErrors errors={[errors['rates'] ?? null]} />}
                        <HierarchyRates rates={rates} sells={sells} form={form} onChangeForm={onChangeForm} totalHierarchyErrors={totalHierarchyErrors} />
                    </>
                )}
            </LoadingCard>
        </div>
    );
}

RatesForm.propTypes = {
    rates: PropTypes.array.isRequired,
    sells: PropTypes.oneOf([
        PropTypes.shape({
            rates: PropTypes.object,
            rate_groups: PropTypes.object
        }),
        null
    ]).isRequired,
    loadingRates: PropTypes.bool,
    form: PropTypes.object.isRequired,
    onChangeForm: PropTypes.func,
    errors: PropTypes.array
};
